import { z } from 'zod';
import { isValid } from 'date-fns';
import { parseDateString } from '@utils/parsers/stringToDate';
import { isBeforeDate } from '@utils/validate';

export const requiredMessage = 'Campo obrigatório.';
export const invalidDateMessage = 'Data inválida.';

const nonEmptyStringSchema = z
  .string({ required_error: requiredMessage })
  .min(1, requiredMessage);

const placeSchema = z.object({
  city: z.object(
    {
      name: nonEmptyStringSchema,
    },
    { required_error: requiredMessage },
  ),
  state: z
    .object(
      {
        name: nonEmptyStringSchema,
        acronym: nonEmptyStringSchema,
      },
      { required_error: requiredMessage },
    )
    .default({
      name: '',
      acronym: '',
    }),
});

export const optionalDateSchema = z
  .string({ required_error: requiredMessage })
  .default('')
  .transform((val, ctx) => {
    if (!val) return '';

    const date = parseDateString(val);

    if (val.length !== 10) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateMessage,
      });

      return '';
    }

    if (!isValid(date)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: invalidDateMessage,
      });
      return '';
    }

    return val;
  });

const waypointsSchema = z
  .object({
    pickup: z.object({
      place: placeSchema,
      date: optionalDateSchema,
    }),
    delivery: z.object({
      place: placeSchema,
      date: optionalDateSchema,
    }),
  })
  .refine(
    (fields) => {
      if (!fields.pickup.date) return true;
      if (!fields.delivery.date) return true;

      const pickupDate = fields.pickup.date;
      const deliveryDate = fields.delivery.date;

      if (isBeforeDate(deliveryDate, pickupDate)) {
        return false;
      }

      return true;
    },
    {
      message: invalidDateMessage,
      path: ['delivery.date'],
    },
  );

const truckerSchema = z.object({
  id: z.string({ required_error: requiredMessage }).min(5, requiredMessage),
  name: z.string({ required_error: requiredMessage }).min(5, requiredMessage),
});

const createJourneySchema = z.object({
  product: z.object({
    name: z.string().default('').optional(),
  }),
  trucker: truckerSchema,
  waypoints: waypointsSchema,
});

export type CreateJourneyType = z.infer<typeof createJourneySchema>;

export default createJourneySchema;
